import useTranslation from 'next-translate/useTranslation'
import { FC, useEffect, useRef, useState } from 'react'
import { FlexboxGrid } from 'rsuite'
import { useRouter } from 'next/router'
import InfoOutline from '@rsuite/icons/InfoOutline'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import useGetAnalyticsPageLocation from '../../services/analytics/useGetAnalyticsPageLocation'
import { useCart } from '../Cart/CartProvider'
import { useCustomer } from '../BuyerProfile/Account/CustomerProvider'
import { selectCartAddress } from '../Cart/selectors'
import { Product, ProductQuantity } from '../../utils/types/Product'
import SwitchVatIcon from '../Icons/SwitchVatIcon'
import { CustomerType } from '../CustomerTypeSelection'
import SafeTranslate from '../common/SafeTranslate'
import { getTaxRate } from '../../lib/resources/price'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import { formatPriceNumber } from '../../utils/math'
import { getTotalProductQuantity } from '../../lib/product'
import usePrice from '../../services/usePrice'
import { getPricePerPieceUnit, getSalesUnit, getSalesUnitBreakdown } from '../../lib/salesUnit'
import NameTagContract from '../CustomPricing/NameTagContract'
import { useAuth } from '../../services/useAuth'
import TakeRateInfoPopover from './TakeRateInfoPopover'
import DiscountPercentageTag from './DiscountPercentageTag'
import { createCookie } from '../../utils/cookies'
import selectCustomerTypeEvent from '../../utils/analytics/selectCustomerTypeEvent'
import SalesUnitSizesPopover from './SalesUnitSizesPopover'

import styles from '../../styles/ProductPriceSection.module.less'

interface ProductPriceSectionProps {
  product: Product
  productQuantity: ProductQuantity[]
}

const ProductPriceSection: FC<ProductPriceSectionProps> = (props) => {
  const { product, productQuantity } = props

  const [animatePrice, setAnimatePrice] = useState(false)
  const [isDiscountActive, setIsDiscountActive] = useState(false)

  const oldPriceRef = useRef('0.00')
  const { locale: countryAndLocale } = useRouter()
  const { t } = useTranslation('products')
  const { user, isVatIncluded, setIsVatIncluded } = useAuth()
  const { cartState: { cart }, updateAddress } = useCart()
  const { updateCustomerType } = useCustomer()

  const analyticsLocation = useGetAnalyticsPageLocation()

  const sppPriceSubrowV3 = useFeatureIsOn('spp-price-subrow-v3')

  const { country } = getCountryAndLocaleStrings(countryAndLocale)
  const taxRate = `${formatPriceNumber(getTaxRate(country), 1)}%`
  const productPricingUnit = getPricePerPieceUnit(product)

  const totalQuantity = getTotalProductQuantity(productQuantity)
  const salesUnit = getSalesUnit(product)
  const salesUnitDescription = `${t('Price per')} ${getSalesUnitBreakdown(product, t, salesUnit)}`

  const {
    default: {
      price: defaultPrice,
    },
    final: {
      price: finalProductPrice,
      reference: finalReferencePrice,
    },
    discount: {
      current: currentDiscount,
      lowest: lowestCustomTierDiscount,
    },
    currency,
    hasCustomPrices,
  } = usePrice(product, totalQuantity)

  const isRFQProduct = !defaultPrice
  const referencePrice = formatPriceNumber(finalReferencePrice)
  const productPrice = formatPriceNumber(finalProductPrice)

  if (animatePrice) {
    // Always reset animation class immediately when it has been triggered
    setTimeout(() => setAnimatePrice(false), 500)
  }
  if (oldPriceRef.current !== productPrice) {
    // Price updated, trigger animation
    oldPriceRef.current = productPrice
    setAnimatePrice(true)
  }

  useEffect(() => {
    if (isVatIncluded) {
      setAnimatePrice(true)
    }
  }, [isVatIncluded])

  useEffect(() => {
    if (animatePrice && !isDiscountActive) {
      setIsDiscountActive(true)
    }
  }, [animatePrice])

  const updateCartVatStatus = (): Promise<void> | ReturnType<typeof updateAddress> => {
    if (cart) {
      const { deliveryAddress, billingAddress } = selectCartAddress(cart)
      if (deliveryAddress && billingAddress) {
        return updateAddress({ deliveryAddress, billingAddress })
      }
    }
    return Promise.resolve()
  }

  const handleToggleVatIncluded = async (selectedCustomerType: Extract<CustomerType, 'business' | 'home'>) => {
    setIsVatIncluded(selectedCustomerType !== 'business')
    createCookie('customerType', selectedCustomerType, 365)
    selectCustomerTypeEvent({ context: analyticsLocation, customerType: selectedCustomerType })

    // Refresh cart to get correct delivery prices
    await updateCartVatStatus()

    // Update user data in database if user is logged in
    if (user) {
      await updateCustomerType(selectedCustomerType)
    }
  }

  const renderProductPrice = () => {
    if (isRFQProduct) {
      return (
        <b>
          {t('Price available on request')}
        </b>
      )
    }

    return (
      <p className={`h2 ${styles['final-price']} ${isDiscountActive && totalQuantity > 1 ? styles['discount-active'] : ''} ${animatePrice ? styles.change : ''}`}>
        {`${productPrice} ${currency}`}
      </p>
    )
  }

  const renderPriceSubrow = () => {
    if (sppPriceSubrowV3) {
      return (
        <FlexboxGrid
          align="middle"
          className={`${styles['per-sales-unit-price']} ${styles['per-sales-unit-price-v3']}`}
        >
          <p>
            {salesUnitDescription}
            <SalesUnitSizesPopover product={product} />
          </p>

          {!isRFQProduct && (
            <p className={`margin-zero ${styles['per-sales-unit-price-v3-reference-price']}`}>
              <span>
                {referencePrice}
              </span>
  &nbsp;
              {currency}
              {' / '}
              {t(productPricingUnit, { count: 1 })}
            </p>
          )}
        </FlexboxGrid>
      )
    }

    return (
      <FlexboxGrid
        align="middle"
        className={styles['per-sales-unit-price']}
      >
        <p className={styles['per-sales-unit-text']}>{salesUnitDescription}</p>

        <br />

        {!isRFQProduct && (
          <p className="margin-zero">
            <span>
              {referencePrice}
            </span>
  &nbsp;
            {currency}
            {' / '}
            {t(productPricingUnit, { count: 1 })}
          </p>
        )}
      </FlexboxGrid>
    )
  }

  return (
    <div className={styles['product-price-section']}>
      <FlexboxGrid className="flex-flow-row">
        <span>{t(isVatIncluded ? 'Prices incl {{amount}} VAT' : 'Prices excl VAT', { amount: taxRate })}</span>

        <FlexboxGrid.Item
          className={styles['switch-vat-button']}
          onClick={() => handleToggleVatIncluded(isVatIncluded ? 'business' : 'home')}
        >
          <FlexboxGrid align="middle">
            <SwitchVatIcon />
            <span>{ t(isVatIncluded ? 'Switch to VAT excl' : 'Switch to VAT incl') }</span>
          </FlexboxGrid>
        </FlexboxGrid.Item>
      </FlexboxGrid>

      <FlexboxGrid align="middle">
        {renderProductPrice()}
        <TakeRateInfoPopover>
          <DiscountPercentageTag
            referencePrice={finalProductPrice}
            fullPrice={defaultPrice}
            currency={currency}
            className={`${animatePrice ? styles.change : ''} ${currentDiscount ? 'bulk-discount-active' : ''}`}
          />
        </TakeRateInfoPopover>
        <TakeRateInfoPopover>
          <InfoOutline className={styles['info-icon']} />
        </TakeRateInfoPopover>
      </FlexboxGrid>

      {renderPriceSubrow()}

      {hasCustomPrices && (
        <FlexboxGrid align="middle">
          <p className={`margin-right-spacer ${styles['per-sales-unit-price']}`}>
            <span>
              {referencePrice}
            </span>
 &nbsp;
            {currency}
            {' / '}
            {t(productPricingUnit, { count: 1 })}
          </p>

          <NameTagContract
            name={user?.company ?? ''}
            tooltipText={(
              <SafeTranslate
                ns="products"
                i18nKey="{{buyer}} contract price for bulk purchasing starting from {{count}} {{salesUnit}} contract price available only for a limited time as per agreement"
                values={{ buyer: user?.company, count: lowestCustomTierDiscount?.['price.quantity'], salesUnit: t(salesUnit, { count: lowestCustomTierDiscount?.['price.quantity'] }) }}
                components={{ br: <br /> }}
              />
            )}
            customStyleFirstLetter={styles['contract-tag-first-letter']}
          />
        </FlexboxGrid>
      )}
    </div>
  )
}

export default ProductPriceSection
